import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, Grid, useTheme, alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as SupplierIcon,
  ShoppingCart as PurchasingIcon,
  ReceiptLong as InvoiceIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import venueSupplierService from '../../../../services/venueSupplierService';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import invoiceService from '../../../../services/invoiceService';
import type { Supplier, PurchaseOrder, Invoice } from '../../../../types/venue';
import type { SupplierItemLink } from '../../../../types/supplierItemLink';

const formatCurrency = (n: number | undefined, currency: string | undefined) => {
  if (n == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
  } catch {
    return n.toFixed(2);
  }
};

const formatDate = (s: string | undefined) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString();
};

/**
 * Venue-scoped supplier detail. All sections are filtered to the current
 * venue. The supplier profile is read-only — global supplier edits stay on
 * /admin/suppliers (admin-only).
 */
const VenueSupplierDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const { supplierId } = useParams<{ supplierId: string }>();
  const backPath = `/admin/venues/${venueId}/suppliers`;

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [links, setLinks] = useState<SupplierItemLink[]>([]);
  const [openPOs, setOpenPOs] = useState<PurchaseOrder[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId || !supplierId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      venueSupplierService.getVenueSupplier(venueId, supplierId),
      venueSupplierService.getVenueSupplierItemLinks(venueId, supplierId),
      // Open POs: use the venue-scoped PO list with supplierId filter. Status
      // filter omitted — show all to give a quick activity overview, but cap
      // page size since this is a sidebar context.
      purchaseOrderService
        .getOrders(venueId, 1, 25, undefined, supplierId)
        .then(r => r.data ?? [])
        .catch(() => [] as PurchaseOrder[]),
      invoiceService
        .getInvoices(venueId, 1, 25, undefined, supplierId)
        .then(r => r.data ?? [])
        .catch(() => [] as Invoice[]),
    ])
      .then(([sup, lks, pos, invs]) => {
        if (cancelled) return;
        setSupplier(sup);
        setLinks(lks);
        setOpenPOs(pos);
        setInvoices(invs);
      })
      .catch(err => {
        if (cancelled) return;
        const msg =
          err?.response?.status === 404
            ? 'Supplier is not connected to this venue.'
            : err?.response?.data?.message ?? err?.message ?? 'Failed to load supplier';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [venueId, supplierId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error ?? 'Supplier not found.'}</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)}>
            Back to suppliers
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(backPath)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <SupplierIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight={600}>{supplier.name}</Typography>
            {supplier.shortName && (
              <Typography variant="body2" color="text.secondary">{supplier.shortName}</Typography>
            )}
          </Box>
          <Chip
            label={supplier.active ? 'Active' : 'Inactive'}
            size="small"
            color={supplier.active ? 'success' : 'default'}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<PurchasingIcon />}
            onClick={() => navigate(`/admin/venues/${venueId}/purchasing/create?supplierId=${supplier._id}`)}
            sx={{ textTransform: 'none' }}
          >
            New PO
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {supplier.type.replace('_', ' ')}
            </Typography>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Country</Typography>
            <Typography variant="body2">{supplier.country || '—'}</Typography>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Account ref</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {supplier.accountReference || '—'}
            </Typography>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Contact</Typography>
            <Typography variant="body2">{supplier.contactName || '—'}</Typography>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Email</Typography>
            <Typography variant="body2">{supplier.contactEmail || '—'}</Typography>
          </Grid>
          <Grid component="div" sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <Typography variant="caption" color="text.secondary">Phone</Typography>
            <Typography variant="body2">{supplier.contactPhone || '—'}</Typography>
          </Grid>
          {supplier.website && (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
              <Typography variant="caption" color="text.secondary">Website</Typography>
              <Typography variant="body2">{supplier.website}</Typography>
            </Grid>
          )}
          {supplier.notes && (
            <Grid component="div" sx={{ gridColumn: { xs: 'span 12' } }}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{supplier.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Linked items at this venue */}
      <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Linked items</Typography>
          <Typography variant="caption" color="text.secondary">at this venue</Typography>
        </Box>
        {links.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No items at this venue link to this supplier yet.</Typography>
            <Typography variant="caption">Link items from the Inventory page.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Supplier code</TableCell>
                  <TableCell>Last cost</TableCell>
                  <TableCell>Last received</TableCell>
                  <TableCell>Preferred</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map(link => (
                  <TableRow
                    key={link._id}
                    hover
                    onClick={() => navigate(`/admin/venues/${venueId}/inventory/${link.inventoryItemId}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{link.displayName ?? link.inventoryItemId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {link.supplierItemCode || link.supplierReference || '—'}
                    </TableCell>
                    <TableCell>{formatCurrency(link.lastReceivedCost, defaultCurrency)}</TableCell>
                    <TableCell>{formatDate(link.lastReceivedDate)}</TableCell>
                    <TableCell>
                      {link.isPreferred ? <Chip label="Preferred" size="small" color="primary" /> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Open POs at this venue */}
      <Paper sx={{ borderRadius: 2, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <PurchasingIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Purchase orders</Typography>
          <Typography variant="caption" color="text.secondary">at this venue</Typography>
        </Box>
        {openPOs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No purchase orders for this supplier at this venue.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PO #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expected delivery</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openPOs.map(po => (
                  <TableRow
                    key={po._id}
                    hover
                    onClick={() => navigate(`/admin/venues/${venueId}/purchasing/${po._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {po._id.slice(-6).toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <Chip label={po.status} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(po.createdAt)}</TableCell>
                    <TableCell>{formatDate(po.expectedDeliveryDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Invoices at this venue */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <InvoiceIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Invoices</Typography>
          <Typography variant="caption" color="text.secondary">at this venue</Typography>
        </Box>
        {invoices.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No invoices for this supplier at this venue.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issue date</TableCell>
                  <TableCell>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow
                    key={inv._id}
                    hover
                    onClick={() => navigate(`/admin/venues/${venueId}/purchasing/invoices`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {inv.invoiceNumber || inv._id.slice(-6).toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <Chip label={inv.status} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(inv.invoiceDate ?? undefined)}</TableCell>
                    <TableCell>{formatCurrency(inv.totalAmount ?? undefined, inv.currencyCode ?? defaultCurrency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default VenueSupplierDetail;
